import { Button } from "@/components/ui/baseUi/button"
import { AssignedCoachInput } from "@/components/ui/forms/assignedCoachInput"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { Text } from "@/components/ui/baseUi/text"
import {
  getDefaultSettingsSchema,
  type BoxProgramDefaultSettingsFormFields,
} from "@/modules/boxProgrammation/formik/boxProgramFormTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { InformationAlertIcon } from "@images/svgIcons/informationAlertIcon"
import { DropInIcon, DropInSelectedIcon } from "@images/svgIcons/slots/drop-in"
import { MultiSlotsIcon, MultiSlotsSelectedIcon } from "@images/svgIcons/slots/multi-slots"
import { TrialSessionIcon, TrialSessionSelectedIcon } from "@images/svgIcons/slots/trial-session"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MAX, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@modules/boxSlot/boxSlotTypes"
import {
  getCancellationBeginningOptions,
  getRegistrationBeginningOptions,
  getRegistrationEndOptions,
  getSlotDurationOptions,
} from "@modules/boxSlotTemplate/boxSlotTemplateUtils"
import { word } from "@modules/core/utils/i18n"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUTimeDropdownOrCustomizedTimeInput } from "@modules/ui/components/huDropdownWithCustomizedTimeInput"
import { LoaderCircle } from "lucide-react"

export const BoxProgramDefaultSettingsForm = withForm({
  defaultValues: {} as BoxProgramDefaultSettingsFormFields,
  props: {} as {
    moveToPreviousFormStep: () => void
    onValidate: () => void
    isSubmitting: boolean
  } & React.ComponentProps<"form">,
  render: function BoxProgramDefaultSettingsFormInner({
    form,
    onValidate,
    isSubmitting,
    moveToPreviousFormStep,
    className,
    ...props
  }) {
    const language = useLanguage()
    const durationOptions = getSlotDurationOptions()
    const registrationBeginningOptions = getRegistrationBeginningOptions()
    const registrationEndOptions = getRegistrationEndOptions()
    const cancellationBeginningOptions = getCancellationBeginningOptions()
    const defaultSettingsSchema = getDefaultSettingsSchema(language)

    return (
      <form
        className={cn("flex flex-col gap-6", className)}
        onSubmit={(e) => {
          e.preventDefault()
          onValidate()
        }}
        {...props}
      >
        <div className="mb-4 flex flex-col gap-2">
          <Text fontStyle="TL">{word("box.activityDefaultValues.form.title")}</Text>
          <HUCardAlert
            title={""}
            description={word("box.activityDefaultValues.form.explanation")}
            icon={
              <InformationAlertIcon
                width={30}
                height={30}
                className="shrink-0"
                backgroundColor="var(--surface-inverted)"
                color="var(--primary-600)"
              />
            }
          />
        </div>

        <form.AppField
          name="duration"
          validators={{ onChange: defaultSettingsSchema.shape.duration, onBlur: defaultSettingsSchema.shape.duration }}
        >
          {(field) => (
            <field.Field>
              <field.Label required>{word("box.slotTemplateGroup.form.slotDuration.label")}</field.Label>
              <field.Content>
                <HUTimeDropdownOrCustomizedTimeInput
                  name="duration"
                  dropdownOptions={durationOptions}
                  getFieldProps={() => ({
                    name: "duration",
                    value: field.state.value,
                    onChange: () => {
                      return
                    },
                    onBlur: field.handleBlur,
                  })}
                  setFieldValue={(_name, value) => {
                    field.handleChange(value as number)
                    return Promise.resolve()
                  }}
                />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        <form.AppField
          name="participantLimit"
          validators={{
            onChange: defaultSettingsSchema.shape.participantLimit,
            onBlur: defaultSettingsSchema.shape.participantLimit,
          }}
        >
          {(field) => (
            <field.Field>
              <field.Label required>{word("box.slotTemplateGroup.form.participantLimit.label")}</field.Label>
              <field.Content>
                <div className="flex flex-row items-center gap-2">
                  <field.NumberInput
                    step={1}
                    max={BOX_SLOT_PARTICIPANTS_LIMIT_MAX}
                    min={BOX_SLOT_PARTICIPANTS_LIMIT_MIN}
                    placeholder={word("box.slotTemplateGroup.form.participantLimit.placeholder")}
                  />
                  <Text fontStyle="BM">
                    {word("box.slotTemplateGroup.form.participantLimit.unit", {
                      count: field.state.value ?? 0,
                    })}
                  </Text>
                </div>
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        <form.Subscribe selector={(s) => [s.values.dropinsAccepted, s.values.trialsAccepted]}>
          {([dropinsAccepted, trialsAccepted]) => (
            <div className="flex flex-row items-center justify-between gap-2">
              {/* Drop in accepted */}
              <div
                className={cn(
                  "flex w-full cursor-pointer flex-col items-center justify-between gap-2 self-stretch rounded-2xl border-solid px-2 py-3 transition-all duration-100",
                  dropinsAccepted
                    ? "border border-borderPrimary"
                    : "border border-borderDefault hover:border-borderDefault-hover",
                )}
                onClick={() => form.setFieldValue("dropinsAccepted", !dropinsAccepted)}
              >
                {dropinsAccepted ? <DropInSelectedIcon color="" /> : <DropInIcon color="" />}
                <Text
                  fontStyle="TS"
                  className={cn("text-center", dropinsAccepted ? "text-textDefault-body" : "text-textDisabled")}
                >
                  {dropinsAccepted
                    ? word("box.slotTemplateGroup.form.dropIn.allowed")
                    : word("box.slotTemplateGroup.form.dropIn.rejected")}
                </Text>
              </div>

              {/* Trial session accepted */}
              <div
                className={cn(
                  "flex w-full cursor-pointer flex-col items-center justify-between gap-2 self-stretch rounded-2xl border-solid px-2 py-3 transition-all duration-100",
                  trialsAccepted
                    ? "border border-borderPrimary"
                    : "border border-borderDefault hover:border-borderDefault-hover",
                )}
                onClick={() => form.setFieldValue("trialsAccepted", !trialsAccepted)}
              >
                {trialsAccepted ? <TrialSessionSelectedIcon color="" /> : <TrialSessionIcon color="" />}
                <Text
                  fontStyle="TS"
                  className={cn("text-center", trialsAccepted ? "text-textDefault-body" : "text-textDisabled")}
                >
                  {trialsAccepted
                    ? word("box.slotTemplateGroup.form.trialSession.allowed")
                    : word("box.slotTemplateGroup.form.trialSession.rejected")}
                </Text>
              </div>
            </div>
          )}
        </form.Subscribe>

        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="uppercase">
            {word("box.slotTemplateGroup.form.registration.label")}
            <Text fontStyle="LM" className="text-red-500">
              {" *"}
            </Text>
          </Text>
          <Text fontStyle="BS">{word("box.slotTemplateGroup.form.registration.description")}</Text>
          <div className="flex gap-2">
            <form.AppField
              name="limitStart"
              validators={{
                onChange: defaultSettingsSchema.shape.limitStart,
                onBlur: defaultSettingsSchema.shape.limitStart,
              }}
            >
              {(field) => (
                <field.Field>
                  <field.Label>{word("box.slotTemplateGroup.form.registration.beginning")}</field.Label>
                  <field.Content>
                    <HUTimeDropdownOrCustomizedTimeInput
                      name="limitStart"
                      dropdownOptions={registrationBeginningOptions}
                      getFieldProps={() => ({
                        name: "limitStart",
                        value: field.state.value as number,
                        onChange: () => {
                          return
                        },
                        onBlur: field.handleBlur,
                      })}
                      setFieldValue={(_name, value) => {
                        field.handleChange(value as number)
                        return Promise.resolve()
                      }}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
            <form.AppField
              name="limitEnd"
              validators={{
                onChange: defaultSettingsSchema.shape.limitEnd,
                onBlur: defaultSettingsSchema.shape.limitEnd,
              }}
            >
              {(field) => (
                <field.Field>
                  <field.Label>{word("box.slotTemplateGroup.form.registration.end")}</field.Label>
                  <field.Content>
                    <HUTimeDropdownOrCustomizedTimeInput
                      name="limitEnd"
                      dropdownOptions={registrationEndOptions}
                      getFieldProps={() => ({
                        name: "limitEnd",
                        value: field.state.value as number,
                        onChange: () => {
                          return
                        },
                        onBlur: field.handleBlur,
                      })}
                      setFieldValue={(_name, value) => {
                        field.handleChange(value as number)
                        return Promise.resolve()
                      }}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="uppercase">
            {word("box.slotTemplateGroup.form.cancellation.label")}
            <Text fontStyle="LL" className="text-red-500">
              {" *"}
            </Text>
          </Text>
          <Text fontStyle="BS">{word("box.slotTemplateGroup.form.cancellation.description")}</Text>
          <form.AppField
            name="limitCancel"
            validators={{
              onChange: defaultSettingsSchema.shape.limitCancel,
              onBlur: defaultSettingsSchema.shape.limitCancel,
            }}
          >
            {(field) => (
              <field.Field>
                <field.Label>{word("box.slotTemplateGroup.form.cancellation.beginning")}</field.Label>
                <field.Content>
                  <HUTimeDropdownOrCustomizedTimeInput
                    name="limitCancel"
                    dropdownOptions={cancellationBeginningOptions}
                    getFieldProps={() => ({
                      name: "limitCancel",
                      value: field.state.value as number,
                      onChange: () => {
                        return
                      },
                      onBlur: field.handleBlur,
                    })}
                    setFieldValue={(_name, value) => {
                      field.handleChange(value as number)
                      return Promise.resolve()
                    }}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>

        <form.Subscribe selector={(s) => s.values.allowMultiBooking}>
          {(allowMultiBooking) => (
            <div
              className={cn(
                "transition-duration-100 flex w-full cursor-pointer flex-row gap-4 rounded-3xl border-solid p-6 transition-all",
                allowMultiBooking
                  ? "border border-borderPrimary"
                  : "border border-borderDefault hover:border-borderDefault-hover",
              )}
              onClick={() => form.setFieldValue("allowMultiBooking", !allowMultiBooking)}
            >
              <div className="flex w-[45%] shrink-0 flex-col items-center justify-center gap-3">
                {allowMultiBooking ? <MultiSlotsSelectedIcon color="" /> : <MultiSlotsIcon color="" />}
                <Text
                  fontStyle="TS"
                  className={`text-center ${allowMultiBooking ? "text-textDefault-body" : "text-textDisabled"}`}
                >
                  {allowMultiBooking
                    ? word("box.slotTemplateGroup.form.mutliSlotBooking.allowed")
                    : word("box.slotTemplateGroup.form.mutliSlotBooking.rejected")}
                </Text>
              </div>
              <Text fontStyle="BS" className="pre-line flex w-[55%] text-textDefault-caption">
                {word("box.slotTemplateGroup.form.mutliSlotBooking.description")}
              </Text>
            </div>
          )}
        </form.Subscribe>

        <form.AppField
          name="location"
          validators={{
            onChange: defaultSettingsSchema.shape.location,
            onBlur: defaultSettingsSchema.shape.location,
          }}
        >
          {(field) => (
            <field.Field>
              <field.Label className="uppercase">{word("box.slotTemplateGroup.form.area.title")}</field.Label>
              <field.Description>{word("box.slotTemplateGroup.form.area.subtitle")}</field.Description>
              <field.Content>
                <field.Input placeholder={word("box.slotTemplateGroup.form.area.placeholder")} />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        <div className="flex flex-col">
          <form.AppField
            name="assignedStaffMembers"
            validators={{
              onChange: defaultSettingsSchema.shape.assignedStaffMembers,
              onBlur: defaultSettingsSchema.shape.assignedStaffMembers,
            }}
          >
            {(field) => (
              <field.Field>
                <field.Label className="uppercase">
                  {word("box.slotTemplateGroup.form.assignedStaffMembers.label")}
                </field.Label>
                <field.Content>
                  <AssignedCoachInput
                    label=""
                    textDescription={word("box.slotTemplateGroup.form.assignedStaffMembers.description")}
                    name="assignedStaffMembers"
                    isRequiredInput={false}
                    getFieldProps={() => ({
                      name: "assignedStaffMembers",
                      value: field.state.value,
                      onChange: () => {
                        return
                      },
                      onBlur: field.handleBlur,
                    })}
                    setFieldValue={(_name, value) => {
                      field.handleChange(value as typeof field.state.value)
                      return Promise.resolve()
                    }}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>

        <div className="mt-4 mb-12 flex justify-end">
          <div className="flex w-full grow flex-col gap-4 sm:flex-row">
            <Button variant="outline" size="lg" onClick={moveToPreviousFormStep} className="grow">
              {word("global.previous")}
            </Button>

            <Button
              isLoading={isSubmitting}
              variant="primary"
              size="lg"
              className="grow"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting && <LoaderCircle className="animate-spin" />}
              {word("global.save")}
            </Button>
          </div>
        </div>
      </form>
    )
  },
})
